import * as Sentry from "@sentry/react";
import axios from "axios";
import moment from "moment-timezone";
import {useContext, useEffect, useRef} from "react";

import HTTPContext from "../../contexts/HTTPContext";
import {UserContext} from "../../contexts/UserContext";
import {useBuildAxiosError} from "../useBuildAxiosError";
import {useToast} from "../useToast";

export const useHttpInterceptor = () => {
    const http = useContext(HTTPContext);
    const {isAuthenticated, user, loggedInTime, setState} = useContext(UserContext);
    const toast = useToast();
    const buildError = useBuildAxiosError();
    const interceptorRef = useRef<number | null>(null);

    useEffect(() => {
        if (isAuthenticated) {
            if (typeof interceptorRef.current === "number") {
                http.interceptors.response.eject(interceptorRef.current);
            }

            const interceptor = http.interceptors.response.use(
                response => response,
                error => {
                    if (axios.isAxiosError(error)) {
                        // take user to login page
                        if (error.response?.status === 403) {
                            if (interceptorRef.current) {
                                http.interceptors.response.eject(interceptorRef.current);
                            }
                            if (loggedInTime) {
                                // track and send this as an issue to Sentry in case of
                                // session got expired in less than 1 hours
                                if (moment(loggedInTime).isAfter(moment().subtract(1, "hour"))) {
                                    Sentry.captureMessage("User session is expired < 1 hour", {
                                        user: {
                                            id: user._id
                                        },
                                        extra: {
                                            endpoint: error.request.url,
                                            loggedInTime,
                                            expiredTime: new Date(),
                                            error
                                        }
                                    });
                                }
                            }
                            setState(prev => ({...prev, isAuthenticated: false}));
                            toast.error(
                                buildError(error, "Your session is expired. Please login again")
                            );
                            return;
                        }
                        // probably add the blacklist
                        toast.error(buildError(error, "An issue occurred. Please try again"));
                    }
                    throw error;
                }
            );

            interceptorRef.current = interceptor;
        }

        return () => {
            if (typeof interceptorRef.current === "number") {
                http.interceptors.response.eject(interceptorRef.current);
            }
        };
    }, [http, isAuthenticated, loggedInTime, user._id, setState, toast, buildError]);
};
